import React, { useCallback, useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import CommonService from "../../services/common.service";
import moment from "moment";

function InvoiceGeneratedGraph({ depo }) {
  const [loading, setLoading] = useState(false);
  const [activeYearly, setActiveYearly] = useState(1); //1-7days, 2-Monthly, 3-Yearly
  const [vechType, setVechType] = useState("1"); //1-SNT 2-PVT

  const location = useLocation();
  const isActiveCentral =
    location.pathname === "/" || location.pathname === "/centralDashboard";

  const [graphDataNew, setGraphDataNew] = useState([]);

  const fetchChallanCount = useCallback(() => {
    setLoading(true);
    const payload = {
      locid: depo,
      type: activeYearly.toString(),
      vechType: vechType,
    };
    CommonService.invoiceGeneration(payload)
      .then((resp) => {
        setLoading(false);

        setGraphDataNew(resp.data);
      })
      .catch((e) => console.log(e));
  }, [activeYearly, location.pathname, vechType]);

  useEffect(() => {
    fetchChallanCount();
  }, [fetchChallanCount, activeYearly, vechType]);

  // console.log(graphDataNew);

  const handleYearlyFilter = (from) => {
    setActiveYearly(from);
  };

  const generateLast7DaysData = () => {
    if (graphDataNew.length != 0) {
      const data = graphDataNew[0].data1.map((item) =>
        moment(item.amounttype, "YYYY-MM-DD").format("DD-MM-YY")
      );

      return data;
    } else {
      return;
    }
  };

  const generateLast7YearsData = () => {
    if (graphDataNew.length != 0) {
      const data = graphDataNew[0].data1.map((itm) => itm.amounttype);
      return data;
    } else {
      return;
    }
  };

  const months = Array.from({ length: dayjs().month() + 1 }, (_, i) =>
    dayjs().month(i).format("MMM")
  );

  const data =
    activeYearly === 1
      ? generateLast7DaysData()
      : activeYearly === 2
      ? months
      : activeYearly === 3
      ? generateLast7YearsData()
      : [];

  const colors = [
    "rgba(240, 174, 74, 0.76)",
    "rgb(103, 235, 235, 0.76)",
    "rgba(202, 88, 105, 0.7)",
    "rgba(247, 255, 142, 0.72)",
    "rgba(255, 232, 138, 0.76)",
    "rgb(61, 177, 197, 0.66)",
    "rgba(173, 145, 228, 0.88)",
    "rgba(236, 137, 214, 1)",
    "rgba(93, 240, 74, 0.77)",
    "rgb(103, 145, 235)",
    "rgb(202, 88, 173)",
    "rgb(74, 228, 125)",
  ];

  const allDatasets = Array.isArray(graphDataNew)
    ? graphDataNew.map((item, index) => ({
        label: isActiveCentral ? item.depo : "Amount",
        data: item.data1.map((item) => item.amount),
        backgroundColor: colors[index],
        categoryPercentage: activeYearly === 2 ? 0.2 : 0.34,
        borderWidth: 1,
        borderColor: "white",
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 4,
          bottomRight: 4,
        },
        borderSkipped: false,
      }))
    : null;

  const chartData = {
    labels: data,
    datasets: allDatasets,
  };

  const formatter = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  });

  const footer = (tooltipItems) => {
    let sum = 0;
    // console.log(tooltipItems);
    sum = tooltipItems.reduce((summ, item) => {
      return summ + item.parsed.y || 0;
    }, 0);

    if (
      location.pathname === "/" ||
      location.pathname === "/centralDashboard"
    ) {
      return `Total Amt: ₹${formatter.format(sum)}\n`;
    } else {
      return;
    }
  };

  const options = {
    type: "bar",
    stacked: true,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        displayColors: true,
        yAlign: "bottom",
        backgroundColor: "rgba(254, 235, 230, 1)",
        titleColor: "black",
        bodyColor: "black",
        footerColor: "black",

        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            const value = context.formattedValue;
            return `${label}: ₹${value}`;
          },
          beforeBody: footer,
        },
        bodyFont: {
          size: 17,
          family: "Jost, sans-serif",
        },
        titleFont: {
          size: 16,
          family: "Jost, sans-serif",
        },
      },
      legend: {
        display: isActiveCentral ? true : false,
        position: "top",
        align: "end",
        labels: {
          boxWidth: 13,
          useBorderRadius: true,
          borderRadius: "6px",
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          drawOnChartArea: true,
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          font: {
            size: 13,
            weight: "bold",
            family: "Jost",
          },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          drawOnChartArea: true,
        },
        ticks: {
          beginAtZero: true,
        },
        suggestedMin: 0,
        suggestedMax: 10,
        title: {
          display: true,
          text: "Amount (in ₹)",
        },
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
  };

  return (
    <>
      <div className="graph-header">
        <div className="graph-heading">Invoice Generated</div>
        <div className="graph-yearly-filter">
          <div
            className={activeYearly === 1 ? "filter-box active" : "filter-box"}
            onClick={() => handleYearlyFilter(1)}
          >
            Last 7 Days
          </div>
          <div
            className={activeYearly === 2 ? "filter-box active" : "filter-box"}
            onClick={() => handleYearlyFilter(2)}
          >
            Monthly
          </div>
          <div
            className={activeYearly === 3 ? "filter-box active" : "filter-box"}
            onClick={() => handleYearlyFilter(3)}
          >
            Yearly
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <label class="radio-container">
            SNT
            <input
              type="radio"
              checked={vechType === "1"}
              onChange={() => {
                setVechType("1");
              }}
              name="snt"
            />
            <span class="checkmark"></span>
          </label>
          <label class="radio-container">
            PVT
            <input
              type="radio"
              checked={vechType === "2"}
              onChange={() => {
                setVechType("2");
              }}
              name="pvt"
            />
            <span class="checkmark"></span>
          </label>
        </div>
      </div>
      <div className="mt-3" style={{ height: "100%" }}>
        {loading ? (
          <>
            <div className="loading-area">
              <div
                class="spinner-border"
                style={{ color: "rgb(247, 177, 140)" }}
                role="status"
              >
                <span class="sr-only"></span>
              </div>
              <span>Data is Loading...</span>
            </div>
          </>
        ) : graphDataNew.length != 0 ? (
          <>
            <Bar data={chartData} options={options} width={600} height={275} />
          </>
        ) : (
          <div className="text-center">No Records Found </div>
        )}
      </div>
    </>
  );
}

export default InvoiceGeneratedGraph;
